import { ReactNode, useState } from 'react'

import { AstraAlertDialog } from '~/astra'
import { useStore } from '~/store/store'
import { useDateRangeDates, usePractitionerStatusDefinitions } from '~/storm'
import { isDepartmentIncluded } from '~/utils/surgeryActivities'

import { DatePractitionerCell } from '../../shared'
import { createGridKeyboardHandler } from '../../shared/createGridKeyboardHandler'
import { toggleStatus } from '../Modal/Statuses/toggleStatus'
import { CellClipboard, createCellClipboard, deleteSelectedActivities, pasteClipboardAtCell } from './keyboardActions'

type Props = {
    selectedCells: DatePractitionerCell[]
    children: ReactNode
    disabled?: boolean
}

type ConfirmOptions = {
    title: string
    description: ReactNode
    cancelButtonLabel: string
    confirmButtonLabel: string
}

type Confirmation = ConfirmOptions & {
    resolve: (confirmed: boolean) => void
}

type PractitionerStatusDefinition = ReturnType<typeof usePractitionerStatusDefinitions>[number]

function matchesShortcut(status: PractitionerStatusDefinition, keyCode: string, isMetaKeyPressed: boolean, isShiftKeyPressed: boolean) {
    const shortcut = status.keyboard_shortcut
    if (!shortcut) return false
    if ((status.keyboard_has_meta_key ?? false) !== isMetaKeyPressed) return false
    if ((status.keyboard_has_shift ?? false) !== isShiftKeyPressed) return false

    // Letters are reported as KeyA, KeyB, etc. Special keys use their code directly, such as Equal or Minus.
    return `Key${shortcut}` === keyCode || shortcut === keyCode
}

export const KeyboardInteraction = ({ selectedCells, children, disabled = false }: Props) => {
    const [clipboard, setClipboard] = useState<CellClipboard | null>(null)
    const [confirmation, setConfirmation] = useState<Confirmation | null>(null)
    const departmentKey = useStore(state => state.appFilters.departmentKey)
    const visibleDates = useDateRangeDates()
    const statusDefinitions = usePractitionerStatusDefinitions({ include: { departments: true } })

    function confirm(options: ConfirmOptions) {
        return new Promise<boolean>(resolve => {
            setConfirmation({ ...options, resolve })
        })
    }

    function closeConfirmation(confirmed: boolean) {
        const resolve = confirmation?.resolve
        setConfirmation(null)
        resolve?.(confirmed)
    }

    function captureClipboard(action: CellClipboard['action']) {
        const nextClipboard = createCellClipboard(selectedCells, action)
        if (nextClipboard) setClipboard(nextClipboard)
    }

    const handleKeyDown = createGridKeyboardHandler({
        disabled: disabled || confirmation !== null || selectedCells.length === 0,
        onDelete: () => {
            void deleteSelectedActivities(selectedCells, confirm).catch(console.error)
        },
        onCopy: () => captureClipboard('copy'),
        onCut: () => captureClipboard('cut'),
        onPaste: () => {
            const firstCell = selectedCells.at(0)
            if (!firstCell || !clipboard) return
            void pasteClipboardAtCell(clipboard, firstCell, visibleDates, confirm)
                .then(consumed => {
                    if (consumed) setClipboard(current => (current === clipboard ? null : current))
                })
                .catch(console.error)
        },
        getShortcut: event =>
            statusDefinitions.find(
                status =>
                    isDepartmentIncluded(departmentKey, status.departments) &&
                    matchesShortcut(status, event.code, event.metaKey || event.ctrlKey, event.shiftKey)
            ),
        onToggle: status => {
            void toggleStatus(selectedCells, status.code).catch(console.error)
        },
    })

    return (
        <div className="contents" onKeyDownCapture={handleKeyDown}>
            {children}
            <AstraAlertDialog
                isOpen={confirmation !== null}
                title={confirmation?.title ?? ''}
                description={confirmation?.description ?? ''}
                confirmButtonLabel={confirmation?.confirmButtonLabel ?? ''}
                cancelButtonLabel={confirmation?.cancelButtonLabel ?? ''}
                onConfirm={() => closeConfirmation(true)}
                onCancel={() => closeConfirmation(false)}
            />
        </div>
    )
}
